/**
 * Template Parser Utilities
 *
 * Functions for extracting and validating template variables in prompts
 */

export interface TemplateError {
  type: 'syntax' | 'variable';
  message: string;
  position: number;
}

export interface ValidationResult {
  isValid: boolean;
  error: string;
}

export type SegmentType = 'text' | 'variable';

export interface TextSegment {
  type: 'text';
  text: string;
}

export interface VariableSegment {
  type: 'variable';
  text: string;
  name: string;
}

export type TemplateSegment = TextSegment | VariableSegment;

export interface VariableInfo extends ValidationResult {
  name: string;
}

export interface TemplateMeta {
  variables: VariableInfo[];
  variableCount: number;
  hasErrors: boolean;
  errors: TemplateError[];
  isValid: boolean;
}

export interface TemplateDiff {
  added: Set<string>;
  removed: Set<string>;
  unchanged: Set<string>;
}

/**
 * Extract all variables from a template string
 * Variables are in the format {{variable_name}}
 *
 * @param template - Template string
 * @returns Set of variable names
 */
export const extractVariables = (template: string): Set<string> => {
  if (!template) return new Set();

  const regex = /\{\{(\w+)\}\}/g;
  const variables = new Set<string>();
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    variables.add(match[1]);
  }

  return variables;
};

/**
 * Validate a variable name
 *
 * @param varName - Variable name to validate
 * @returns Validation result with isValid flag and error message
 */
export const validateVariableName = (varName: string): ValidationResult => {
  if (!varName) {
    return { isValid: false, error: 'Variable name cannot be empty' };
  }

  // Must start with letter or underscore
  if (!/^[a-zA-Z_]/.test(varName)) {
    return {
      isValid: false,
      error: 'Variable name must start with a letter or underscore'
    };
  }

  // Must contain only alphanumeric and underscores
  if (!/^[a-zA-Z_]\w*$/.test(varName)) {
    return {
      isValid: false,
      error: 'Variable name can only contain letters, numbers, and underscores'
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate template syntax
 *
 * @param template - Template to validate
 * @returns Array of template errors
 */
export const validateTemplateSyntax = (template: string): TemplateError[] => {
  if (!template) return [];

  const errors: TemplateError[] = [];

  // Check for unmatched braces
  const openCount = (template.match(/\{\{/g) || []).length;
  const closeCount = (template.match(/\}\}/g) || []).length;

  if (openCount !== closeCount) {
    if (openCount > closeCount) {
      errors.push({
        type: 'syntax',
        message: 'Unmatched opening braces',
        position: template.lastIndexOf('{{')
      });
    } else {
      errors.push({
        type: 'syntax',
        message: 'Unmatched closing braces',
        position: template.lastIndexOf('}}')
      });
    }
  }

  // Check for invalid variable names
  const variables = extractVariables(template);
  Array.from(variables).forEach(varName => {
    const { isValid, error } = validateVariableName(varName);
    if (!isValid) {
      const position = template.indexOf(`{{${varName}}}`);
      errors.push({
        type: 'variable',
        message: `Invalid variable '${varName}': ${error}`,
        position
      });
    }
  });

  return errors;
};

/**
 * Resolve a template by replacing variables with values
 *
 * @param template - Template string
 * @param variables - Variable values
 * @returns Resolved template
 */
export const resolveTemplate = (template: string, variables: Record<string, string> = {}): string => {
  if (!template) return '';

  let resolved = template;
  for (const [varName, varValue] of Object.entries(variables)) {
    resolved = resolved.replaceAll(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), varValue);
  }

  return resolved;
};

/**
 * Compare two templates and identify variable changes
 *
 * @param oldTemplate - Original template
 * @param newTemplate - New template
 * @returns Object with added, removed, and unchanged variable sets
 */
export const compareTemplates = (oldTemplate: string, newTemplate: string): TemplateDiff => {
  const oldVars = extractVariables(oldTemplate || '');
  const newVars = extractVariables(newTemplate || '');

  const added = new Set(Array.from(newVars).filter(v => !oldVars.has(v)));
  const removed = new Set(Array.from(oldVars).filter(v => !newVars.has(v)));
  const unchanged = new Set(Array.from(oldVars).filter(v => newVars.has(v)));

  return { added, removed, unchanged };
};

/**
 * Suggest a corrected variable name
 *
 * @param varName - Invalid variable name
 * @returns Suggested corrected name
 */
export const suggestVariableFix = (varName: string): string => {
  if (!varName) return 'variable';

  // Replace hyphens and spaces with underscores
  let suggested = varName.replaceAll(/[-\s]/g, '_');

  // If starts with number, prepend underscore
  if (/^\d/.test(suggested)) {
    suggested = `_${suggested}`;
  }

  // Remove any remaining invalid characters
  suggested = suggested.replaceAll(/[^\w]/g, '');

  return suggested || 'variable';
};

/**
 * Highlight variables in template for display
 * Returns array of segments with type and text
 *
 * @param template - Template string
 * @returns Array of text and variable segments
 */
export const highlightVariables = (template: string): TemplateSegment[] => {
  if (!template) return [];

  const segments: TemplateSegment[] = [];
  let lastIndex = 0;
  const regex = /\{\{(\w+)\}\}/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    // Add text before variable
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        text: template.substring(lastIndex, match.index)
      });
    }

    // Add variable
    segments.push({
      type: 'variable',
      text: match[0],
      name: match[1]
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < template.length) {
    segments.push({
      type: 'text',
      text: template.substring(lastIndex)
    });
  }

  return segments;
};

/**
 * Get missing variables (required but not provided)
 *
 * @param template - Template string
 * @param providedVariables - Provided variable values
 * @returns Missing variable names
 */
export const getMissingVariables = (template: string, providedVariables: Record<string, string> = {}): string[] => {
  const required = extractVariables(template);
  const provided = new Set(Object.keys(providedVariables));

  return Array.from(required).filter(v => !provided.has(v));
};

/**
 * Parse template and extract metadata
 *
 * @param template - Template string
 * @returns Template metadata
 */
export const parseTemplate = (template: string): TemplateMeta => {
  const variables = extractVariables(template);
  const errors = validateTemplateSyntax(template);
  const variableList: VariableInfo[] = Array.from(variables).map(varName => ({
    name: varName,
    ...validateVariableName(varName)
  }));

  return {
    variables: variableList,
    variableCount: variables.size,
    hasErrors: errors.length > 0,
    errors,
    isValid: errors.length === 0
  };
};
